import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Stack,
  Container,
  Grid,
  Paper,
  Button,
} from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import Iconify from '../components/Iconify';
import BotCatalogCard from './components/Bots/BotCatalogCard';
import BotPricingDetails from './components/Bots/BotPricingDetails';
import BotSetupProgress from './components/Bots/BotSetupProgress';
import BotSuspendedState from './components/Bots/BotSuspendedState';
import WhatsappSettingSection from './components/Settings/WhatsappSettingSection';
import TelegramSettingSection from './components/Settings/TelegramSettingSection';
import {
  getSubscriptions,
  getProducts,
  createSubscription,
  initializePaystackPayment,
  verifyPaystackPayment,
} from '../api/subscriptions.api';

export default function BotsPage() {
  const { botId } = useParams();
  const navigate = useNavigate();

  const { activeApp: reduxActiveApp } = useSelector((state) => state.auth);
  let activeApp = reduxActiveApp;
  if (!activeApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        activeApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          activeApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore
    }
  }

  // Status per bot: 'unactivated' | 'setting_up' | 'active' | 'suspended'
  const [botStatuses, setBotStatuses] = useState({
    whatsapp: 'unactivated',
    telegram: 'unactivated',
    instagram: 'unactivated',
  });

  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);

  const currentAppId = activeApp?._id;

  const loadData = useCallback(async () => {
    try {
      const [subRes, prodRes] = await Promise.all([
        getSubscriptions(),
        getProducts(),
      ]);

      let loadedSubs = [];
      if (subRes && subRes.ok && subRes.data?.data) {
        loadedSubs = subRes.data.data;
      } else if (subRes && Array.isArray(subRes.data)) {
        loadedSubs = subRes.data;
      }

      let loadedProds = [];
      if (prodRes && prodRes.ok && prodRes.data?.data) {
        loadedProds = prodRes.data.data;
      } else if (prodRes && Array.isArray(prodRes.data)) {
        loadedProds = prodRes.data;
      }

      setSubscriptions(loadedSubs);
      setProducts(loadedProds);

      // Filter subscriptions for current app
      const appSubs = currentAppId
        ? loadedSubs.filter(
            (s) => s.app?._id === currentAppId || s.app === currentAppId
          )
        : loadedSubs;

      // WhatsApp subscription mapping
      const waSub = appSubs.find(
        (s) =>
          s.product?.type === 'whatsapp' ||
          (typeof s.product === 'object' && s.product?.name?.toLowerCase().includes('whatsapp'))
      );

      if (waSub) {
        let mappedStatus = 'unactivated';
        if (waSub.status === 'active') mappedStatus = 'active';
        else if (waSub.status === 'pending-setup') mappedStatus = 'setting_up';
        else if (waSub.status === 'suspended' || waSub.status === 'past-due' || waSub.status === 'past_due') mappedStatus = 'suspended';
        else if (waSub.status === 'pending-payment') mappedStatus = 'unactivated';

        setBotStatuses((prev) => ({
          ...prev,
          whatsapp: mappedStatus,
        }));
      } else {
        setBotStatuses((prev) => ({
          ...prev,
          whatsapp: 'unactivated',
        }));
      }
    } catch (err) {
      console.error('Error loading bot subscriptions:', err);
    }
  }, [currentAppId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle Paystack callback verification if redirected back with transaction reference
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const reference = query.get('reference') || query.get('trxref');
    if (reference) {
      const verify = async () => {
        try {
          const res = await verifyPaystackPayment({ reference });
          if (res && res.ok && res.data?.success) {
            toast.success('Payment verified successfully via Paystack!');
          } else {
            const message = res?.data?.message || 'Payment verification failed';
            if (res?.data?.status === 'abandoned') {
              toast.warn('Payment was cancelled or abandoned.');
            } else {
              toast.error(message);
            }
          }
        } catch (err) {
          toast.error('An error occurred during payment verification');
        } finally {
          window.history.replaceState({}, document.title, window.location.pathname);
          loadData();
        }
      };
      verify();
    }
  }, [loadData]);

  const whatsappProduct = products.find((p) => p.type === 'whatsapp');
  const telegramProduct = products.find((p) => p.type === 'telegram');

  const botList = [
    {
      id: 'whatsapp',
      name: 'WhatsApp Cloud Bot',
      description: 'Sell data, airtime and bills in WhatsApp.',
      activationFee: whatsappProduct?.setupFee || 120000,
      maintenanceFee: whatsappProduct?.price || 30000,
      status: 'available',
      brandColor: '#25D366',
      brandBg: '#DCFCE7',
      icon: <Iconify icon="logos:whatsapp-icon" width={28} height={28} />,
    },
    {
      id: 'telegram',
      name: 'Telegram Bot',
      description: 'Execute instant top-ups with custom slash commands and inline menus.',
      monthlyPrice: telegramProduct?.price ?? 10000,
      status: 'coming_soon',
      brandColor: '#229ED9',
      brandBg: '#E0F2FE',
      icon: <Iconify icon="logos:telegram" width={28} height={28} />,
    },
    {
      id: 'instagram',
      name: 'Instagram Direct Bot',
      description: 'Convert direct message inquiries into automated sales and recharge links.',
      monthlyPrice: 20000,
      status: 'coming_soon',
      brandColor: '#E1306C',
      brandBg: '#FCE7F3',
      icon: <Iconify icon="logos:instagram-icon" width={28} height={28} />,
    },
  ];

  const handleSetBotStatus = (id, status) => {
    setBotStatuses((prev) => ({
      ...prev,
      [id]: status,
    }));
  };

  const handleActivateBot = async () => {
    const waProduct = products.find((p) => p.type === 'whatsapp');
    if (!waProduct || !activeApp?._id) {
      toast.error('Unable to initiate bot activation: WhatsApp product or active app not found.');
      return;
    }

    try {
      const appSubs = activeApp
        ? subscriptions.filter(
            (s) => s.app?._id === activeApp._id || s.app === activeApp._id
          )
        : subscriptions;

      const waSub = appSubs.find(
        (s) =>
          s.product?._id === waProduct._id ||
          s.product?.type === 'whatsapp' ||
          (typeof s.product === 'object' && s.product?.name?.toLowerCase().includes('whatsapp'))
      );

      let subId = waSub?._id;

      if (!subId) {
        const res = await createSubscription({
          productId: waProduct._id,
          appId: activeApp._id,
        });
        if (res && (res.ok || res.status === 201) && res.data?.data) {
          subId = res.data.data.subscription?._id || res.data.data._id;
        }
      }

      if (!subId) {
        toast.error('Failed to create subscription record. Please try again.');
        return;
      }

      toast.info('Connecting to Paystack checkout portal...');
      const initRes = await initializePaystackPayment(subId, {
        months: 1,
        callbackUrl: window.location.href,
      });

      if (initRes && initRes.ok && initRes.data?.data?.authorization_url) {
        window.location.href = initRes.data.data.authorization_url;
        return;
      }

      toast.error(initRes?.data?.message || 'Failed to initialize Paystack checkout.');
    } catch (e) {
      console.error('Error initiating bot payment:', e);
      toast.error('Failed to connect to Paystack payment gateway.');
      throw e;
    }
  };

  const handleReactivateBot = async () => {
    const appSubs = activeApp
      ? subscriptions.filter(
          (s) => s.app?._id === activeApp._id || s.app === activeApp._id
        )
      : subscriptions;

    const waSub = appSubs.find(
      (s) =>
        s.product?.type === 'whatsapp' ||
        (typeof s.product === 'object' && s.product?.name?.toLowerCase().includes('whatsapp'))
    );

    if (!waSub?._id) {
      toast.error('Subscription not found for reactivation.');
      return;
    }

    try {
      toast.info('Connecting to Paystack checkout portal...');
      const initRes = await initializePaystackPayment(waSub._id, {
        months: 1,
        callbackUrl: window.location.href,
      });

      if (initRes && initRes.ok && initRes.data?.data?.authorization_url) {
        window.location.href = initRes.data.data.authorization_url;
        return;
      }

      toast.error(initRes?.data?.message || 'Failed to initialize Paystack checkout.');
    } catch (e) {
      console.error('Error renewing subscription:', e);
      toast.error('Failed to connect to Paystack payment gateway.');
      throw e;
    }
  };

  // If a specific bot is selected in URL
  if (botId) {
    const selectedBot = botList.find((b) => b.id === botId);

    // If invalid bot ID, redirect back to catalog
    if (!selectedBot) {
      return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA', py: 6 }}>
          <Container maxWidth="md">
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '20px' }}>
              <Typography variant="h6" fontWeight={600}>Bot Not Found</Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard/bots')}
                sx={{
                  mt: 2,
                  borderRadius: '12px',
                  textTransform: 'none',
                  bgcolor: '#111827',
                  '&:hover': { bgcolor: '#1F2937' },
                }}
              >
                Return to Bots
              </Button>
            </Paper>
          </Container>
        </Box>
      );
    }

    const currentStatus = botStatuses[botId] || 'unactivated';

    const appSubs = activeApp
      ? subscriptions.filter(
          (s) => s.app?._id === activeApp._id || s.app === activeApp._id
        )
      : subscriptions;

    const currentWaSub = appSubs.find(
      (s) =>
        s.product?.type === 'whatsapp' ||
        (typeof s.product === 'object' && s.product?.name?.toLowerCase().includes('whatsapp'))
    );

    const renderActivatedBotContent = () => {
      if (botId === 'whatsapp') {
        return <WhatsappSettingSection subscription={currentWaSub} activeApp={activeApp} />;
      }
      if (botId === 'telegram') {
        return <TelegramSettingSection />;
      }
      return (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '20px' }}>
          <Typography variant="h6">Configuration coming soon for {selectedBot.name}</Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard/bots')}
            sx={{ mt: 2, textTransform: 'none', borderRadius: '12px' }}
          >
            Back to Bots
          </Button>
        </Paper>
      );
    };

    // STATE 1: UNACTIVATED (PAYWALL / PRICING DETAILS)
    if (currentStatus === 'unactivated') {
      return (
        <Box
          sx={{
            minHeight: 'calc(100vh - 100px)',
            bgcolor: '#FFFFFF',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            py: { xs: 5, sm: 6 },
          }}
        >
          {/* Subtle Top Controls */}
          <Box
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              top: { sm: 28 },
              left: { sm: 36 },
              mb: { xs: 3, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Button
              onClick={() => navigate('/dashboard/bots')}
              startIcon={<ChevronLeft size={18} />}
              sx={{
                color: '#6B7280',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.88rem',
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
                '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
              }}
            >
              All Bots
            </Button>
          </Box>

          {/* Centered Paywall View */}
          <BotPricingDetails
            bot={selectedBot}
            onActivate={handleActivateBot}
          />
        </Box>
      );
    }

    // STATE 2: SETTING UP (SUPER ADMIN PROVISIONING AFTER PAYMENT)
    if (currentStatus === 'setting_up') {
      return (
        <Box
          sx={{
            minHeight: 'calc(100vh - 100px)',
            bgcolor: '#FFFFFF',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            py: { xs: 5, sm: 6 },
          }}
        >
          {/* Subtle Top Controls */}
          <Box
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              top: { sm: 28 },
              left: { sm: 36 },
              mb: { xs: 3, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Button
              onClick={() => navigate('/dashboard/bots')}
              startIcon={<ChevronLeft size={18} />}
              sx={{
                color: '#6B7280',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.88rem',
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
                '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
              }}
            >
              All Bots
            </Button>
          </Box>

          {/* Centered Apple-aesthetic Setup Tracker */}
          <BotSetupProgress
            bot={selectedBot}
            onCompleteSetup={(id) => handleSetBotStatus(id, 'active')}
          />
        </Box>
      );
    }

    // STATE 4: SUSPENDED (OVERDUE MONTHLY MAINTENANCE)
    if (currentStatus === 'suspended') {
      return (
        <Box
          sx={{
            minHeight: 'calc(100vh - 100px)',
            bgcolor: '#FFFFFF',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: { xs: 2, sm: 4 },
            py: { xs: 5, sm: 6 },
          }}
        >
          {/* Subtle Top Controls */}
          <Box
            sx={{
              position: { xs: 'static', sm: 'absolute' },
              top: { sm: 28 },
              left: { sm: 36 },
              mb: { xs: 3, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Button
              onClick={() => navigate('/dashboard/bots')}
              startIcon={<ChevronLeft size={18} />}
              sx={{
                color: '#6B7280',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.88rem',
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
                '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
              }}
            >
              All Bots
            </Button>
          </Box>

          {/* Centered Suspended View */}
          <BotSuspendedState
            bot={selectedBot}
            onReactivate={handleReactivateBot}
          />
        </Box>
      );
    }

    // STATE 3: ACTIVE (FULL OPERATIONAL SETTINGS)
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FBFBFD', py: { xs: 3, sm: 4 } }}>
        <Container maxWidth="md">
          <Stack spacing={3}>
            {/* Top Navigation Bar: Back button */}
            <Box>
              <Button
                onClick={() => navigate('/dashboard/bots')}
                startIcon={<ChevronLeft size={18} />}
                sx={{
                  color: '#6B7280',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.88rem',
                  borderRadius: '20px',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
                }}
              >
                All Bots
              </Button>
            </Box>

            {renderActivatedBotContent()}
          </Stack>
        </Container>
      </Box>
    );
  }

  // DEFAULT VIEW: APPLE MINIMALIST CATALOG (CARDS GRID)
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA', py: 5 }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          {/* Header */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  color: '#111827',
                  letterSpacing: '-0.025em',
                }}
              >
                Bots
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6B7280',
                  mt: 0.5,
                  fontSize: '0.95rem',
                }}
              >
                Connect automated channels to process customer orders 24/7.
              </Typography>
            </Box>
          </Stack>

          {/* Clean Bot Cards Grid */}
          <Grid container spacing={3} justifyContent="center">
            {botList.map((bot) => (
              <Grid item xs={12} sm={6} md={4} key={bot.id}>
                <BotCatalogCard
                  bot={bot}
                  botStatus={botStatuses[bot.id] || 'unactivated'}
                  isActivated={botStatuses[bot.id] === 'active'}
                  onSelect={(id) => navigate(`/dashboard/bots/${id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}
